<template>
  <div class="ex-hold-acts">
    <button
      v-if="hold.dealRegistrationId"
      type="button"
      class="btn primary"
      @click="emit('view-deal', hold.dealRegistrationId)"
    >
      {{ t('exclusiveUnits.release.viewDeal') }}
    </button>
    <button
      v-else
      type="button"
      class="btn primary"
      @click="continueToDeal(hold)"
    >
      {{ t('exclusiveUnits.release.continue') }}
    </button>
    <button
      type="button"
      class="btn ghost"
      @click="openConfirm"
    >
      {{ t('exclusiveUnits.release.button') }}
    </button>

    <ModalShell
      v-if="confirming"
      :labelled-by="titleId"
      :dismissable="!isReleasing"
      @close="confirming = false"
    >
      <h2
        :id="titleId"
        class="mb-2 pe-6 font-serif text-[22px] font-semibold leading-[1.2] tracking-[-0.015em] text-black-100"
      >
        {{ t('exclusiveUnits.release.confirmTitle', { unit: hold.unitNumber }) }}
      </h2>
      <p class="mb-5 text-[14px] text-black-60">{{ t('exclusiveUnits.release.confirmBody') }}</p>
      <p
        v-if="errorKey"
        class="mb-4 text-[13px] font-medium text-red-600"
        role="alert"
      >
        {{ t(`exclusiveUnits.release.errors.${errorKey}`) }}
      </p>
      <div class="flex flex-wrap gap-2.5">
        <button
          type="button"
          class="inline-flex h-11 flex-1 items-center justify-center rounded-[10px] bg-black-100 px-5 text-[14px] font-bold text-white transition-colors hover:bg-black-90 disabled:opacity-50"
          :disabled="isReleasing"
          @click="onRelease"
        >
          {{ isReleasing ? t('exclusiveUnits.release.releasing') : t('exclusiveUnits.release.confirm') }}
        </button>
        <button
          type="button"
          class="inline-flex h-11 flex-1 items-center justify-center rounded-[10px] border border-black-10 bg-white px-5 text-[14px] font-semibold text-black-100 transition-colors hover:bg-black-05 disabled:opacity-50"
          :disabled="isReleasing"
          @click="confirming = false"
        >
          {{ t('exclusiveUnits.release.cancel') }}
        </button>
      </div>
    </ModalShell>
  </div>
</template>

<script setup lang="ts">
import ModalShell from '~/components/property/access/modals/ModalShell.vue';
import { useExclusiveHoldActions } from '~/composables/useExclusiveHoldActions';
import { releaseErrorKey } from '~/utils/exclusives/holdDialog';
import type { ExclusiveHoldRow } from '~/utils/exclusives/holdRows';

const { hold } = defineProps<{
  hold: ExclusiveHoldRow;
}>();

const emit = defineEmits<{
  (e: 'released', holdId: string): void;
  (e: 'view-deal', dealId: string): void;
}>();

const { t } = useI18n();
const titleId = useId();
const { continueToDeal, releaseHold, isReleasing } = useExclusiveHoldActions();

const confirming = ref(false);
const errorKey = ref<string | null>(null);

const openConfirm = () => {
  errorKey.value = null;
  confirming.value = true;
};

const onRelease = async () => {
  if (isReleasing.value) return;
  errorKey.value = null;
  try {
    await releaseHold(hold.holdId);
    confirming.value = false;
    emit('released', hold.holdId);
  } catch (error) {
    errorKey.value = releaseErrorKey(error);
  }
};
</script>

<style scoped lang="scss">
.ex-hold-acts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 0 14px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;

  &.primary {
    background: #0d0f15;
    color: #fff;
    border: none;
  }

  &.ghost {
    background: #fff;
    color: #0d0f15;
    border: 1px solid rgba(13, 15, 21, 0.15);
  }
}
</style>
